/**
 * Inventory library tabs (docs/inventory.md): Overview · Items · Kits · Assets · Stock, then
 * Counts · Yearly requests · Alerts · Setup. The active tab lives in `?tab=`; older values from the
 * count hub (`scheduled`, `yearly-requests`) keep working so stored links and bookmarks land right.
 *
 * Tabs are built in phases. Until a tab is built it stays hidden and every link to it goes to the
 * page that serves it today, so this file is the one place where each link's target switches.
 */

export type InventoryTabKey =
  | 'overview'
  | 'items'
  | 'kits'
  | 'assets'
  | 'stock'
  | 'counts'
  | 'yearly'
  | 'alerts'
  | 'setup';

/** The page grant a tab needs (docs/inventory.md, "Who can see what"). */
export type InventoryPageKey = 'admin_inventory' | 'inventory' | 'yearly_inventory' | 'alerts';

export type InventoryTabDefinition = {
  key: InventoryTabKey;
  label: string;
  /** Shorter label for phones. */
  shortLabel: string;
  /** Any one of these page grants shows the tab. */
  pages: readonly InventoryPageKey[];
  /** Starts the second group (the strip draws a divider before it). */
  groupStart?: boolean;
  /** False until the tab's phase lands; hidden tabs link to `legacyHref` instead. */
  built: boolean;
  legacyHref?: string;
};

export const INVENTORY_PAGE_KEYS: readonly InventoryPageKey[] = ['admin_inventory', 'inventory', 'yearly_inventory', 'alerts'];

export const INVENTORY_TABS: readonly InventoryTabDefinition[] = [
  { key: 'overview', label: 'Overview', shortLabel: 'Overview', pages: INVENTORY_PAGE_KEYS, built: true },
  { key: 'items', label: 'Items', shortLabel: 'Items', pages: ['admin_inventory'], built: true },
  { key: 'kits', label: 'Kits', shortLabel: 'Kits', pages: ['admin_inventory'], built: true },
  { key: 'assets', label: 'Assets', shortLabel: 'Assets', pages: ['admin_inventory'], built: true },
  { key: 'stock', label: 'Stock', shortLabel: 'Stock', pages: ['admin_inventory'], built: true },
  { key: 'counts', label: 'Physical Inventory', shortLabel: 'Physical Inventory', pages: ['inventory'], groupStart: true, built: true },
  { key: 'yearly', label: 'Yearly requests', shortLabel: 'Yearly', pages: ['yearly_inventory'], built: true },
  { key: 'alerts', label: 'Alerts', shortLabel: 'Alerts', pages: ['alerts'], built: true },
  { key: 'setup', label: 'Setup', shortLabel: 'Setup', pages: ['admin_inventory'], built: true },
];

export type InventoryTabRequest = {
  tab: InventoryTabKey;
  /** True when the URL used an old or unknown value and should be rewritten. */
  legacy: boolean;
};

/** Reads `?tab=`; unknown or missing values fall back to the Overview. */
export function parseInventoryTab(raw: string | null | undefined): InventoryTabRequest {
  const value = String(raw ?? '').trim().toLowerCase();
  switch (value) {
    case 'overview':
    case 'items':
    case 'kits':
    case 'assets':
    case 'stock':
    case 'counts':
    case 'yearly':
    case 'alerts':
    case 'setup':
      return { tab: value, legacy: false };
    // The count hub's tab values (before 2026-10).
    case 'scheduled':
    case 'inventories':
      return { tab: 'counts', legacy: true };
    case 'yearly-requests':
    case 'yearly_requests':
      return { tab: 'yearly', legacy: true };
    default:
      return { tab: 'overview', legacy: value.length > 0 };
  }
}

type HrefExtra = { [key: string]: string | null | undefined };

function withParams(path: string, params: URLSearchParams): string {
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

/** A link to a tab. `site` (the `?site=` value) keeps the viewer's site picker across tabs. */
export function inventoryTabHref(tab: InventoryTabKey, extra?: HrefExtra): string {
  const definition = INVENTORY_TABS.find((entry) => entry.key === tab);
  const params = new URLSearchParams();
  if (definition && !definition.built && definition.legacyHref) return definition.legacyHref;
  if (tab !== 'overview') params.set('tab', tab);
  Object.entries(extra ?? {}).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  return withParams('/inventory', params);
}

/** Record links. */
export const inventoryRecordHref = {
  item: (itemId: string, section?: string) => `/inventory/items/${encodeURIComponent(itemId)}${section ? `/${section}` : ''}`,
  kit: (kitId: string, section?: string) => `/inventory/kits/${encodeURIComponent(kitId)}${section ? `/${section}` : ''}`,
  /** New items are still created on the item editor page, which opens the record once saved. */
  newItem: () => '/admin/items/editor?itemId=new&tab=basics&returnTo=inventory',
  asset: (assetId: string, section?: string) => `/inventory/assets/${encodeURIComponent(assetId)}${section ? `/${section}` : ''}`,
  /** A count's manager record (Review, Signoff, Reports). */
  count: (runId: string, section: 'review' | 'signoff' | 'reports' = 'review') =>
    `/inventory/counts/${encodeURIComponent(runId)}/${section}`,
  /** The counting screen, unchanged by the redesign. */
  countWork: (runId: string) => `/inventory-runs/${encodeURIComponent(runId)}`,
  yearlyWorkspace: (campaignId: string) => `/yearly-inventory/campaigns/${encodeURIComponent(campaignId)}/workspace`,
  yearlySiteRequest: (campaignId: string, locationId: string, requestId: string) =>
    `/yearly-inventory/campaigns/${encodeURIComponent(campaignId)}/workspace/overview?locationId=${encodeURIComponent(locationId)}&requestId=${encodeURIComponent(requestId)}`,
  yearlyReview: (campaignId: string) => `/yearly-inventory/campaigns/${encodeURIComponent(campaignId)}/review`,
  yearlyMaster: (campaignId: string) => `/yearly-inventory/campaigns/${encodeURIComponent(campaignId)}/workspace/overview?master=1`,
  yearlyPlannedPurchases: (campaignId: string) => `/yearly-inventory/campaigns/${encodeURIComponent(campaignId)}/planned-purchases`,
  yearlyPlannedTransfers: (campaignId: string) => `/yearly-inventory/campaigns/${encodeURIComponent(campaignId)}/planned-transfers`,
  /** Kickoff, campaign counts and delete (the campaigns page). */
  yearlyAdmin: (campaignId: string) => `/yearly-inventory/campaigns/${encodeURIComponent(campaignId)}`,
};
